import type {
  LedgerDayStatus,
  LedgerDriftResolution,
  LedgerGlAccountClass,
  LedgerGlAccountStatus,
  LedgerIntegrityRunTrigger,
  LedgerJournalStatus,
  LedgerNormalBalance,
  LedgerPostingPeriodStatus,
  LedgerPostingSource,
} from '@/api/ledger-types'
import type { BusinessDayStatus } from '@/api/types'
import type { PermissionCode } from '@/lib/permissions'


/**
 * The words the ledger screens use, in one place.
 *
 * Statuses are spelled as the server spells them and translated once, here. Nothing in this file
 * invents a state: every label maps a value the API actually sends, and each map is keyed by the
 * closed union from `ledger-types.ts`, so a member added on the server is a type error at this
 * table rather than a blank cell in front of an accountant.
 */

/**
 * The *current-day* statuses, from `GET /ledger/business-day/current`.
 *
 * Its third member is `NotOpened` — an answer about a branch with no open day. The register's
 * statuses below are a different union whose third member is `Closed`, and the two are kept apart
 * deliberately: a closed day and a day that never opened are opposite facts.
 */
export const BUSINESS_DAY_STATUS_LABEL: Record<BusinessDayStatus, string> = {
  Open: 'Day open',
  /** The date is settled and still the one to display, but nothing new posts into it. */
  Closing: 'Closing',
  NotOpened: 'Not opened',
}

export const BUSINESS_DAY_STATUS_TONE: Record<BusinessDayStatus, 'success' | 'warning'> = {
  Open: 'success',
  Closing: 'warning',
  NotOpened: 'warning',
}

/** The statuses a day carries in the register, once it has been opened at least once. */
export const DAY_STATUS_LABEL: Record<LedgerDayStatus, string> = {
  Open: 'Open',
  Closing: 'Closing',
  Closed: 'Closed',
}

export const DAY_STATUS_TONE: Record<LedgerDayStatus, 'success' | 'warning' | 'neutral'> = {
  Open: 'success',
  Closing: 'warning',
  /** Neutral, not danger. A closed day is the goal, not a fault. */
  Closed: 'neutral',
}

export const POSTING_SOURCE_LABEL: Record<LedgerPostingSource, string> = {
  Teller: 'Teller',
  Loans: 'Loans',
  Savings: 'Savings',
  GeneralLedger: 'Manual journal',
  System: 'System',
  FieldCollection: 'Field collection',
}

export const JOURNAL_STATUS_LABEL: Record<LedgerJournalStatus, string> = {
  Posted: 'Posted',
  Reversed: 'Reversed',
}

export const GL_ACCOUNT_CLASS_LABEL: Record<LedgerGlAccountClass, string> = {
  Asset: 'Asset',
  Liability: 'Liability',
  Equity: 'Equity',
  Income: 'Income',
  Expense: 'Expense',
}

export const NORMAL_BALANCE_LABEL: Record<LedgerNormalBalance, string> = {
  Debit: 'Debit',
  Credit: 'Credit',
}

export const GL_ACCOUNT_STATUS_TONE: Record<LedgerGlAccountStatus, 'success' | 'danger' | 'neutral'> = {
  Active: 'success',
  /** Blocked institution-wide: the chart is not branch-scoped, so this stops every branch. */
  Blocked: 'danger',
  Archived: 'neutral',
}

export const PERIOD_STATUS_LABEL: Record<LedgerPostingPeriodStatus, string> = {
  Open: 'Open',
  SoftClosed: 'Soft closed',
  HardClosed: 'Sealed',
}

export const PERIOD_STATUS_TONE: Record<LedgerPostingPeriodStatus, 'success' | 'warning' | 'neutral'> = {
  Open: 'success',
  SoftClosed: 'warning',
  HardClosed: 'neutral',
}

/** What each period state actually permits, in the one line an operator needs before acting. */
export const PERIOD_STATUS_MEANING: Record<LedgerPostingPeriodStatus, string> = {
  Open: 'Anything may post into this month.',
  SoftClosed:
    'System-generated postings and reversals still land. A manual journal is refused, whatever permission the operator holds.',
  HardClosed:
    'Sealed. Nothing posts into this month at all — its numbers have been reported. Only a countersigned reopening can change that.',
}

export const DRIFT_RESOLUTION_LABEL: Record<LedgerDriftResolution, string> = {
  Unresolved: 'Unresolved',
  Repaired: 'Repaired',
  AcceptedWithExplanation: 'Accepted with explanation',
}

export const DRIFT_RESOLUTION_TONE: Record<LedgerDriftResolution, 'danger' | 'success' | 'warning'> = {
  /** Danger, and it earns it: an unresolved alarm means a reported figure may be wrong. */
  Unresolved: 'danger',
  Repaired: 'success',
  AcceptedWithExplanation: 'warning',
}

/** What choosing each disposition asserts. Shown in the dialog, above the choice. */
export const DRIFT_RESOLUTION_MEANING: Record<'Repaired' | 'AcceptedWithExplanation', string> = {
  Repaired:
    'The stored rollup was wrong and has been put right from the postings underneath it. The note records what was wrong, so the same fault can be recognised again.',
  AcceptedWithExplanation:
    'The difference stands and you are asserting the figure is nonetheless correct. The note is the whole of this control — your name goes against it in the register.',
}

export const DRIFT_TRIGGER_LABEL: Record<LedgerIntegrityRunTrigger, string> = {
  Nightly: 'Nightly job',
  OnDemand: 'Run by hand',
  DayClose: 'Day close',
}

/**
 * `AuditSource` and `AuditOutcome` cross the wire as integers — the API's own enums carry no string
 * converter and the contract types both as `integer`. They are mapped by number here, and an
 * unknown code renders as the number rather than as nothing, so a member added on the server shows
 * up as unfamiliar instead of silently blank.
 */
export const AUDIT_SOURCE_LABEL: Record<number, string> = {
  1: 'Web',
  2: 'Mobile',
  3: 'API',
  4: 'System',
}

export const AUDIT_OUTCOME_LABEL: Record<number, string> = {
  1: 'Succeeded',
  2: 'Failed',
  3: 'Denied',
}

export const AUDIT_OUTCOME_TONE: Record<number, 'success' | 'danger' | 'warning'> = {
  1: 'success',
  2: 'danger',
  3: 'warning',
}

export const auditSourceLabel = (code: number): string => AUDIT_SOURCE_LABEL[code] ?? `Source ${code}`

export const auditOutcomeLabel = (code: number): string => AUDIT_OUTCOME_LABEL[code] ?? `Outcome ${code}`

/**
 * The entity types the ledger's own work is recorded under in the audit trail, with the shape of
 * the identifier each one is filed by. These are read from the handlers that write them, not
 * guessed: the trail is keyed by `entityType` and `entityId` exactly as the writer supplied them,
 * and a lookup with the wrong shape returns an empty page rather than an error.
 */
export interface LedgerAuditSubject {
  entityType: string
  label: string
  /** What the identifier looks like, shown as the field's help text. */
  identifierHint: string
  /** Where the identifier comes from, in one line. */
  provenance: string
}

export const LEDGER_AUDIT_SUBJECTS: LedgerAuditSubject[] = [
  {
    entityType: 'JournalEntry',
    label: 'Journal entry',
    identifierHint: 'The entry number, exactly as the ledger issued it.',
    provenance:
      'Every posting is recorded under its entry number, and so is its reversal. The journal screen shows the number on every row.',
  },
  {
    entityType: 'BusinessDay',
    label: 'Business day',
    identifierHint: 'Branch identifier and date joined by a colon — 1:2026-08-27.',
    provenance:
      'Written when a day is opened and again when it is closed. The business-day screen builds this identifier for you.',
  },
  {
    entityType: 'LoanProvisionRun',
    label: 'Provision run',
    identifierHint: 'The run’s public identifier (UUID).',
    provenance:
      'Written on every classification run — but the run’s response does not carry its own identifier, so this is only usable when you already hold one.',
  },
]

/**
 * What the ledger is catalogued to do and still cannot do from this application, with the
 * permission each capability sits under and the reason it is unreachable.
 *
 * The list is short now, and that is the point of keeping it: a back-office accountant who cannot
 * find a control should learn whether it is missing from the API, missing from this client, or
 * missing from their own access — and never be shown a plausible-looking empty table in place of a
 * capability that does not exist. In a banking application a figure on screen is a claim, and there
 * is no way for a reader to tell an invented one from a real one.
 */
export interface LedgerCapabilityGap {
  title: string
  /** The catalogued permission the capability would be gated on. */
  permission: PermissionCode
  detail: string
}

/**
 * Empty, and emptied one entry at a time rather than deleted: chart maintenance gained its
 * endpoints (`chart-maintenance.tsx`), drift disposition gained `ledger.drift.resolve`, the day
 * reopening gained `ledger.day.reopen` (`reopen-business-day.tsx`), and journals now carry the
 * poster's name resolved through the Access seam. The machinery stays because the next gap will
 * need naming the same way — see `ledger-gaps.tsx` for why a named absence beats an implied one.
 */
export const LEDGER_CAPABILITY_GAPS: LedgerCapabilityGap[] = []

/**
 * A provision rate is a fraction in 0–1 on the wire. Rendered as a percentage with no more
 * precision than it carries — this is a rate, not money, so it never goes near the pesewa helpers.
 */
const rateFormatter = new Intl.NumberFormat('en-GB', {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

export const formatProvisionRate = (rate: number): string => rateFormatter.format(rate)

/** `31–60 days`, or `90+ days` where the band has no upper edge. */
export const formatOverdueBand = (minDays: number, maxDays: number | null): string =>
  maxDays === null ? `${minDays}+ days` : `${minDays}–${maxDays} days`

/** `2026`, `8` → `August 2026`. Periods are named by their month, never by `2026-08`. */
const monthNameFormatter = new Intl.DateTimeFormat('en-GB', {
  month: 'long',
  year: 'numeric',
  timeZone: 'Africa/Accra',
})

export const formatPeriodName = (year: number, month: number): string =>
  monthNameFormatter.format(new Date(Date.UTC(year, Math.max(0, month - 1), 1)))

/** `2026-08-27` → `August 2026`, for naming the period a date falls in. */
export const formatMonthOf = (isoDate: string): string => monthNameFormatter.format(new Date(isoDate))

/**
 * The browser's date, in the form a date input and the API both use.
 *
 * Used for field defaults and ceilings only. It is never used to decide what day a branch is
 * trading in — that comes from the ledger, and the two can legitimately differ.
 */
export const today = (): string => new Date().toISOString().slice(0, 10)

/** The first day of the month `isoDate` falls in, for defaulting a report's range. */
export const startOfMonth = (isoDate: string): string => `${isoDate.slice(0, 7)}-01`
